import { createTheme } from '@mui/material/styles';
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

import { previewThemes } from '../themes/previewThemes';
import { buildTrendPalette, normalizeTrendScheme, DEFAULT_TREND_SCHEME } from '../themes/trendPalette';
import { 
  convertThemeToCSSVariables, 
  applyCSSVariablesToRoot, 
  injectThemeTransitionStyles,
  performanceMonitor, 
} from '../utils/cssVariables';

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

// Shared typography configuration
const sharedTypography = {
  // Edge/Chrome/Firefox 호환 폰트 스택 - Windows/Korean 최적화
  fontFamily: '"Pretendard", "Noto Sans KR", "Apple SD Gothic Neo", "Malgun Gothic", "맑은 고딕", "Roboto", "Segoe UI", "Helvetica Neue", "Arial", sans-serif',
  fontSize: 14,
  htmlFontSize: 16,
  // Edge/IE 호환성을 위한 폰트 렌더링 개선
  fontDisplay: 'swap',
  h1: {
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h2: {
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.005em',
  },
  h4: {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: 'normal',
  },
  h5: {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: 'normal',
  },
  h6: {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: 'normal',
  },
  subtitle1: {
    fontVariantNumeric: 'tabular-nums',
  },
  subtitle2: {
    fontVariantNumeric: 'tabular-nums',
  },
  body1: {
    lineHeight: 1.6,
    letterSpacing: '0.00938em',
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    letterSpacing: '0.01071em',
    '@media (max-width:600px)': {
      fontSize: '0.8rem',
      lineHeight: 1.4,
    },
  },
  button: {
    letterSpacing: '0.02857em',
    textTransform: 'none',
  },
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.4,
    letterSpacing: '0.03333em',
    '@media (max-width:600px)': {
      fontSize: '0.7rem',
      lineHeight: 1.3,
    },
  },
};

// Shared component styles
const sharedComponents = {
  // CSS Reset 및 크로스브라우저 호환성을 위한 기본 스타일
  MuiCssBaseline: {
    styleOverrides: {
      html: {
        // Edge/IE에서 폰트 렌더링 개선
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        textRendering: 'optimizeLegibility',
        // 브라우저별 스크롤바 통일
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
        },
      },
      body: {
        // Edge에서 폰트 렌더링 최적화
        fontFamily: '"Pretendard", "Noto Sans KR", "Apple SD Gothic Neo", "Malgun Gothic", "맑은 고딕", "Roboto", "Segoe UI", "Helvetica Neue", "Arial", sans-serif',
        fontOpticalSizing: 'auto',
        fontVariationSettings: '"wght" 400',
        // 브라우저별 기본 여백 제거
        margin: 0,
        padding: 0,
      },
      '*': {
        // 브라우저별 박스 모델 통일
        boxSizing: 'border-box',
      },
      '*::before': {
        boxSizing: 'border-box',
      },
      '*::after': {
        boxSizing: 'border-box',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => ({
        background: theme.palette.background.paper,
        backdropFilter: 'none',
        border: theme.palette.mode === 'dark' 
          ? '1px solid rgba(51, 65, 85, 0.3)'
          : '1px solid rgba(226, 232, 240, 0.5)',
        borderRadius: '8px',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 1px 3px 0 rgba(0, 0, 0, 0.3)'
          : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        transition: 'all 0.2s ease',
        // Edge에서 여백 일관성 보장
        '& > *': {
          margin: 0,
        },
      }),
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        background: theme.palette.background.paper,
        backdropFilter: 'none',
        border: theme.palette.mode === 'dark' 
          ? '1px solid rgba(51, 65, 85, 0.3)'
          : '1px solid rgba(226, 232, 240, 0.5)',
        borderRadius: '8px',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 1px 3px 0 rgba(0, 0, 0, 0.3)'
          : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 6px -1px rgba(0, 0, 0, 0.4)'
            : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '6px',
        textTransform: 'none',
        fontWeight: 500,
        transition: 'all 0.2s ease',
        boxShadow: 'none',
        // Edge에서 버튼 스타일 일관성
        fontFamily: 'inherit',
        lineHeight: 1.5,
        letterSpacing: '0.02857em',
        // Edge에서 포커스 스타일 통일
        '&:focus': {
          outline: '2px solid',
          outlineColor: theme.palette.primary.main,
          outlineOffset: '2px',
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none',
        },
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark'
            ? '0 1px 3px 0 rgba(0, 0, 0, 0.3)'
            : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        },
        // Edge에서 disabled 상태 스타일 통일
        '&:disabled': {
          opacity: 0.6,
          cursor: 'not-allowed',
        },
      }),
    },
  },
  // TextField 컴포넌트 크로스브라우저 호환성 개선
  MuiTextField: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Edge에서 텍스트 필드 렌더링 개선
        '& .MuiOutlinedInput-root': {
          fontFamily: 'inherit',
          '& input': {
            // Edge에서 placeholder 스타일 통일
            '&::placeholder': {
              color: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.7)' 
                : 'rgba(0, 0, 0, 0.6)',
              opacity: 1,
            },
            '&::-ms-input-placeholder': {
              color: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.7)' 
                : 'rgba(0, 0, 0, 0.6)',
            },
          },
        },
      }),
    },
  },
  // 숫자 표·KPI 정렬: 자릿수 폭을 고정(tabular-nums) — 표 셀 전체와 KPI 에 쓰는 제목 계열 typography 에 한 번에 적용
  MuiTableCell: {
    styleOverrides: {
      root: {
        fontVariantNumeric: 'tabular-nums',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: ({ theme }) => ({
        // 라이트: primary 면 + contrastText (기존 모양 유지)
        // 다크: 다크 primary 는 대비 확보를 위해 밝게 보정되어 있어 넓은 면(AppBar)에 쓰면 밝은 바가 된다
        //       -> 어두운 표면(paper) + text.primary, 활성 표시는 각 메뉴가 primary 색 밑줄/글자로 처리
        backgroundColor: theme.palette.mode === 'dark'
          ? theme.palette.background.paper
          : theme.palette.primary.main,
        color: theme.palette.mode === 'dark'
          ? theme.palette.text.primary
          : theme.palette.primary.contrastText,
        backdropFilter: 'none',
        borderBottom: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: 'none',
      }),
    },
  },
};

// Light theme configuration
const lightThemeConfig = {
  palette: {
    mode: 'light',
    primary: {
      main: '#4a5568', // 차분한 그레이-블루
      light: '#718096',
      dark: '#2d3748',
    },
    secondary: {
      main: '#5a67d8', // 차분한 인디고
      light: '#7f9cf5',
      dark: '#4c51bf',
    },
    success: {
      main: '#48bb78', // 부드러운 그린
      light: '#68d391',
      dark: '#38a169',
    },
    error: {
      main: '#f56565', // 부드러운 레드
      light: '#fc8181',
      dark: '#e53e3e',
    },
    warning: {
      main: '#ed8936', // 부드러운 오렌지
      light: '#f6ad55',
      dark: '#dd6b20',
    },
    info: {
      main: '#4299e1', // 차분한 블루
      light: '#63b3ed',
      dark: '#3182ce',
    },
    background: {
      default: '#f7fafc', // 밝은 그레이 배경
      paper: '#ffffff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
    },
    // 미니멀한 glass 효과
    glass: {
      primary: 'rgba(255, 255, 255, 0.95)',
      secondary: 'rgba(255, 255, 255, 0.9)',
      border: 'rgba(226, 232, 240, 0.8)',
    },
    gradient: {
      primary: 'linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%)',
      secondary: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
      success: 'linear-gradient(135deg, #c6f6d5 0%, #9ae6b4 100%)',
      info: 'linear-gradient(135deg, #bee3f8 0%, #90cdf4 100%)',
    },
  },
};

// Dark theme configuration
const darkThemeConfig = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#90cdf4', // 부드러운 라이트 블루
      light: '#cbd5e1',
      dark: '#64748b',
    },
    secondary: {
      main: '#a5b4fc', // 부드러운 인디고
      light: '#c7d2fe',
      dark: '#818cf8',
    },
    success: {
      main: '#6ee7b7', // 부드러운 민트
      light: '#86efac',
      dark: '#34d399',
    },
    error: {
      main: '#fca5a5', // 부드러운 코랄
      light: '#fecaca',
      dark: '#f87171',
    },
    warning: {
      main: '#fcd34d', // 부드러운 옆로우
      light: '#fde68a',
      dark: '#fbbf24',
    },
    info: {
      main: '#93c5fd', // 부드러운 스카이 블루
      light: '#bfdbfe',
      dark: '#60a5fa',
    },
    background: {
      default: '#0f172a', // 깨끗한 다크 블루-그레이
      paper: '#1e293b',
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.95)',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    // 다크모드 미니멀 glass 효과
    glass: {
      primary: 'rgba(30, 41, 59, 0.95)',
      secondary: 'rgba(30, 41, 59, 0.9)',
      border: 'rgba(51, 65, 85, 0.5)',
    },
    gradient: {
      primary: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
      secondary: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      success: 'linear-gradient(135deg, #064e3b 0%, #065f46 100%)',
      info: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
    },
  },
};

// Create base themes using Material-UI's createTheme with shared configurations
const lightTheme = createTheme({
  ...lightThemeConfig,
  typography: sharedTypography,
  components: sharedComponents,
});

const darkTheme = createTheme({
  ...darkThemeConfig,
  typography: sharedTypography,
  components: sharedComponents,
});

export const CustomThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // localStorage에서 테마 설정 불러오기
    const savedTheme = localStorage.getItem('theme_mode');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // 시스템 설정 감지
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false; // Default to light mode if matchMedia not available
  });

  // Selected predefined theme state (for 8 professional themes)
  const [selectedThemeIndex, setSelectedThemeIndex] = useState(() => {
    const saved = localStorage.getItem('selected_theme_index');
    return saved ? parseInt(saved, 10) : 0; // Default to Corporate Gray
  });

  // 사용자 정의 테마 상태 추가
  const [customTheme, setCustomTheme] = useState(() => {
    const saved = localStorage.getItem('custom_theme');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // 테마 데이터 유효성 검증
        if (parsed && typeof parsed === 'object' && (parsed.light || parsed.dark)) {
          return parsed;
        }
      } catch (error) {
        console.warn('Invalid custom theme in localStorage, removing:', error);
        localStorage.removeItem('custom_theme');
      }
    }
    return null;
  });

  // 개인화 설정 상태 추가
  const [personalPreferences, setPersonalPreferences] = useState(() => {
    const saved = localStorage.getItem('personal_preferences');
    return saved ? JSON.parse(saved) : {
      glassIntensity: 'medium', // light, medium, strong
      animationSpeed: 'normal', // slow, normal, fast
      compactMode: false, // 컴팩트 레이아웃
      showAnimations: true, // 애니메이션 표시 여부
      accentColor: 'primary', // primary, secondary, success, info
      cardHoverEffect: true, // 카드 호버 효과
      autoThemeSwitch: false, // 시간대별 자동 테마 전환
      trendColorScheme: DEFAULT_TREND_SCHEME, // 등락 색: 'korean'(상승 빨강/하락 파랑) | 'western'(상승 초록/하락 빨강)
      widgetOrder: [
        'portfolio_summary',
        'market_status',
        'recent_transactions',
        'favorite_stocks',
      ], // 위젯 순서
      // Dashboard card visibility settings
      showAssetSummary: true,
      showAccountSummary: true,
      showOptimizedReturnTrend: true,
      showMarketStatus: true,
    };
  });

  // 시스템 테마 변경 감지
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const savedTheme = localStorage.getItem('theme_mode');
      if (!savedTheme) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme_mode', newMode ? 'dark' : 'light');
  };

  const setThemeMode = (mode) => {
    const isDark = mode === 'dark';
    setIsDarkMode(isDark);
    localStorage.setItem('theme_mode', mode);
  };

  const resetToSystemTheme = () => {
    localStorage.removeItem('theme_mode');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(systemPrefersDark);
  };

  // 개인화 설정 업데이트 함수
  const updatePersonalPreferences = (newPreferences) => {
    const updated = { ...personalPreferences, ...newPreferences };
    setPersonalPreferences(updated);
    localStorage.setItem('personal_preferences', JSON.stringify(updated));
  };

  // 개별 설정 업데이트 함수들
  const setGlassIntensity = (intensity) => {
    updatePersonalPreferences({ glassIntensity: intensity });
  };

  const setAnimationSpeed = (speed) => {
    updatePersonalPreferences({ animationSpeed: speed });
  };

  const toggleCompactMode = () => {
    updatePersonalPreferences({ compactMode: !personalPreferences.compactMode });
  };

  const toggleAnimations = () => {
    updatePersonalPreferences({ showAnimations: !personalPreferences.showAnimations });
  };

  const setAccentColor = (color) => {
    updatePersonalPreferences({ accentColor: color });
  };

  const toggleCardHoverEffect = () => {
    updatePersonalPreferences({ cardHoverEffect: !personalPreferences.cardHoverEffect });
  };

  const setTrendColorScheme = (scheme) => {
    updatePersonalPreferences({ trendColorScheme: normalizeTrendScheme(scheme) });
  };

  const toggleAutoThemeSwitch = () => {
    updatePersonalPreferences({ autoThemeSwitch: !personalPreferences.autoThemeSwitch });
  };

  // Predefined theme selection functions
  const selectPredefinedTheme = useCallback((themeIndex) => {
    if (themeIndex >= 0 && themeIndex < previewThemes.length) {
      setSelectedThemeIndex(themeIndex);
      localStorage.setItem('selected_theme_index', themeIndex.toString());
      // Clear custom theme when selecting predefined theme
      if (customTheme) {
        setCustomTheme(null);
        localStorage.removeItem('custom_theme');
      }
    }
  }, [customTheme]);

  const getCurrentThemeName = useCallback(() => {
    if (customTheme && customTheme.name) {
      return customTheme.name;
    }
    const selectedTheme = previewThemes[selectedThemeIndex] || previewThemes[0];
    return selectedTheme.name;
  }, [customTheme, selectedThemeIndex]);

  // 사용자 정의 테마 적용 함수
  const applyCustomTheme = (themeData) => {
    // 테마 데이터 유효성 검증
    if (!themeData || typeof themeData !== 'object' || (!themeData.light && !themeData.dark)) {
      console.error('Invalid theme data provided to applyCustomTheme');
      return;
    }
    
    setCustomTheme(themeData);
    localStorage.setItem('custom_theme', JSON.stringify(themeData));
  };

  // 사용자 정의 테마 제거 함수
  const resetToDefaultTheme = () => {
    setCustomTheme(null);
    localStorage.removeItem('custom_theme');
  };

  // 자동 테마 전환 로직
  useEffect(() => {
    if (personalPreferences.autoThemeSwitch) {
      const hour = new Date().getHours();
      const shouldBeDark = hour < 6 || hour > 18; // 오후 6시 ~ 오전 6시는 다크모드
      if (shouldBeDark !== isDarkMode) {
        setIsDarkMode(shouldBeDark);
      }
    }
  }, [personalPreferences.autoThemeSwitch, isDarkMode]);

  // Initialize CSS transitions and apply initial theme variables
  useEffect(() => {
    // Inject theme transition styles once
    injectThemeTransitionStyles();
  }, []);

  // Apply CSS variables when theme changes
  useEffect(() => {
    performanceMonitor.start();
    
    const root = document.documentElement;
    root.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    
    // Get current theme (predefined or custom)
    let activeTheme;
    if (customTheme) {
      activeTheme = customTheme[isDarkMode ? 'dark' : 'light'];
      root.classList.add('custom-theme-applied');
      root.style.setProperty('--theme-applied', '1');
    } else {
      const selectedTheme = previewThemes[selectedThemeIndex] || previewThemes[0];
      activeTheme = selectedTheme[isDarkMode ? 'dark' : 'light'];
      root.classList.remove('custom-theme-applied');
      root.style.removeProperty('--theme-applied');
    }
    
    // Convert theme to CSS variables and apply them
    if (activeTheme) {
      const cssVariables = convertThemeToCSSVariables(activeTheme);
      applyCSSVariablesToRoot(cssVariables);
      
      // Store chart colors as CSS variables for Chart.js integration
      const selectedTheme = customTheme || previewThemes[selectedThemeIndex] || previewThemes[0];
      if (selectedTheme.chartColors) {
        selectedTheme.chartColors.forEach((color, index) => {
          root.style.setProperty(`--chart-color-${index}`, color);
        });
      }
    }
    
    performanceMonitor.end('css-variables-update');
  }, [isDarkMode, customTheme, selectedThemeIndex]);

  // Theme creation with CSS variables integration
  const currentTheme = useMemo(() => {
    performanceMonitor.start();
    
    let baseTheme;
    
    // Use predefined theme or fallback to default
    if (customTheme) {
      // Custom theme takes precedence
      const customThemeConfig = customTheme[isDarkMode ? 'dark' : 'light'];
      if (customThemeConfig && typeof customThemeConfig === 'object') {
        try {
          baseTheme = createTheme(isDarkMode ? darkTheme : lightTheme, customThemeConfig);
        } catch (error) {
          console.warn('Failed to apply custom theme, using predefined:', error);
          const selectedTheme = previewThemes[selectedThemeIndex] || previewThemes[0];
          baseTheme = selectedTheme[isDarkMode ? 'dark' : 'light'];
        }
      } else {
        baseTheme = isDarkMode ? darkTheme : lightTheme;
      }
    } else {
      // Use selected predefined theme
      const selectedTheme = previewThemes[selectedThemeIndex] || previewThemes[0];
      const selectedThemeConfig = selectedTheme[isDarkMode ? 'dark' : 'light'];
      // Merge with base theme to ensure all required properties
      baseTheme = createTheme(isDarkMode ? darkTheme : lightTheme, selectedThemeConfig.palette ? { palette: selectedThemeConfig.palette } : selectedThemeConfig);
    }
    
    // Apply personal preferences
    const animationSpeeds = {
      slow: '0.5s',
      normal: '0.3s',
      fast: '0.15s',
    };

    // Create final theme with personal preferences and CSS variables support
    // 등락 의미 색 토큰 (gain/loss/flat) — 테마 paper 배경 대비 4.5:1 이상으로 보정
    const trendPalette = buildTrendPalette({
      scheme: normalizeTrendScheme(personalPreferences.trendColorScheme),
      mode: isDarkMode ? 'dark' : 'light',
      paper: baseTheme.palette.background.paper,
      background: baseTheme.palette.background.default,
      textSecondary: baseTheme.palette.text.secondary,
    });

    const finalTheme = createTheme(baseTheme, {
      palette: trendPalette,
      personalPreferences,
      transitions: {
        duration: {
          standard: animationSpeeds[personalPreferences.animationSpeed],
        },
      },
      // Add CSS variables integration
      cssVariables: {
        // Flag to indicate CSS variables are available
        enabled: true,
        // Helper function to get CSS variable
        var: (varName) => `var(--theme-${varName})`,
      },
    });
    
    performanceMonitor.end('theme-creation');
    return finalTheme;
  }, [isDarkMode, customTheme, personalPreferences, selectedThemeIndex]);

  const value = {
    isDarkMode,
    theme: currentTheme,
    toggleTheme,
    setThemeMode,
    resetToSystemTheme,
    // Predefined theme management
    selectedThemeIndex,
    selectPredefinedTheme,
    getCurrentThemeName,
    availableThemes: previewThemes,
    // 커스텀 테마 관련
    customTheme,
    applyCustomTheme,
    resetToDefaultTheme,
    // 개인화 설정 관련
    personalPreferences,
    updatePersonalPreferences,
    setGlassIntensity,
    setAnimationSpeed,
    toggleCompactMode,
    toggleAnimations,
    setAccentColor,
    toggleCardHoverEffect,
    toggleAutoThemeSwitch,
    setTrendColorScheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};